import { Component, Input, TemplateRef, computed, inject, model, output, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { UploadImageComponent } from 'src/app/Images/upload-image/upload-image.component';
import { ConfirmationDialogComponent } from 'src/app/Shared/confirmation-dialog/confirmation-dialog.component';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { getBankLogoByName } from 'src/app/bank.config';
import { ALERT_TYPE, CUSTOMER_DOCUSEAL_TYPE, INJECT_TYPE, SUCCESS_MESSAGES } from 'src/app/constants';
import { DialogItem } from 'src/app/dialog-item';
import { Contact } from 'src/model/contact.model';
import { Customer } from 'src/model/customer.model';
import { CustomerAccount } from 'src/model/customerAccount.model';
import { CustomerAccountService } from 'src/services/customer-account.service';
import { AccountAuthorizationHistoryComponent } from '../accountAuthorizationHistory/accountAuthorizationHistory.component';
import { AddAccountAuthorizationRequestComponent } from '../add-account-authorization-request/add-account-authorization-request.component';
import { DocusealService } from 'src/services/docuseal.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { DocusealHistoryComponent } from 'src/app/Others/docuseal-history/docuseal-history.component';
import { Docuseal } from 'src/model/formJ.model';

@Component({
	selector: 'app-bank-account-detail',
	templateUrl: './bank-account-detail.component.html',
	styleUrl: './bank-account-detail.component.scss',
	standalone: false
})
export class BankAccountDetailComponent {
	protected sharedService = inject(SharedService);
	private router = inject(Router);
	private customerAccountService = inject(CustomerAccountService);
	private docusealService = inject(DocusealService);
	private clipboard = inject(Clipboard);

	accountItem = model.required<CustomerAccount[]>();
	@Input() customer: Customer;
	@Input() contact: Contact;

	addBankAccount = output<CustomerAccount>();

	reason = signal<string>('');
	emptyReason = computed(() => this.reason().length === 0);
	disableReasonTemplate = viewChild.required<TemplateRef<any>>('disableReason');

	readonly INJECT_TYPE = INJECT_TYPE;
	readonly getBankLogoByName = getBankLogoByName;

	contactDetails: Contact;
	signLink: string;

	openAddAuthorizationModal(account: CustomerAccount) {
		const modalRef = this.sharedService.openModal(AddAccountAuthorizationRequestComponent);
		modalRef.componentInstance.customerId = account.CustomerId;
		modalRef.componentInstance.accountId = account.Id;
		modalRef.componentInstance.contactId = account.ContactId;
	}

	openAddBankAccountModal(account: CustomerAccount) {
		this.addBankAccount.emit(Object.assign(new CustomerAccount(), account));
	}

	openAuthorizationHistoryModal(account: CustomerAccount) {
		const modalRef = this.sharedService.openModal(AccountAuthorizationHistoryComponent, 'largeModal');
		modalRef.componentInstance.accountDetails = account;
	}

	openAuthorizationLetter(account: CustomerAccount) {
		const companyId = sessionStorage.getItem('companyId');
		this.router.navigate(['/accountAuthorizationLetter', companyId, account.CustomerId], { state: { account: account, contact: this.contact } });
	}

	openUploadAcctDoc(account: CustomerAccount, type?: string) {
		const modalRef = this.sharedService.openModal(UploadImageComponent);

		modalRef.componentInstance.selectedCustomerId = account.CustomerId;
		modalRef.componentInstance.isCustomerAcctDocument = true;
		modalRef.componentInstance.customerAcctId = account.Id;
		modalRef.componentInstance.custAcctDocType = type;
	}

	disableCustomerAccount(account: CustomerAccount) {
		const modalRef = this.sharedService.openModal(ConfirmationDialogComponent);
		modalRef.componentInstance.message = `Are you sure you want to disable this account?`;
		modalRef.componentInstance.injectContent = [new DialogItem(this.disableReasonTemplate(), { account: account }, INJECT_TYPE.TEMPLATE)];
		modalRef.componentInstance.disableConfirm = this.emptyReason;

		modalRef.result.then(async confirm => {
			if (confirm) {
				const result = await this.customerAccountService.postDisableCustomerAccount(account.CustomerId, account.Id, this.reason());
				this.accountItem.update(item => {
					const i = item.findIndex(a => a.Id === account.Id);
					item.splice(i, 1, result);
					return [...item];
				});
				this.sharedService.openAlert(SUCCESS_MESSAGES.ACTION('disabled', 'customer account'), ALERT_TYPE.SUCCESS);
			}
		});
	}

	async createSignLink(account: CustomerAccount) {
		let data = {
			customerAccountId: account.Id
		};
		let ds = await this.docusealService.getPostAccountAuthorizationDocuseal(this.customer.ID, data);
		if (ds) {
			this.sharedService.openAlert(`Sign link created, please check VIEW SIGN LINK HISTORY to get the link!`, ALERT_TYPE.SUCCESS);
		}
	}

	openSignLink() {
		this.clipboard.copy(this.signLink);
	}
}
